<script setup>
/**
 * The Journal's one edit pattern (journal practice 2026-10-28, DESIGN §7b):
 * the seeker's words with a faint gold ✎ at the end and a soft highlight on
 * hover. Tap → a field right where the words were, the same type and size, on
 * a paper wash with a dashed gold underline; Cancel · Done under it, and one
 * small grey line saying what the edit touches. Enter on a one-line field is
 * Done, Esc is Cancel. After an edit the words carry a tiny EDITED mark.
 *
 * The parent saves (`save` with the new words) and owns the book; this only
 * holds the field while it is open.
 */
import { ref, nextTick, onMounted } from 'vue'

const props = defineProps({
  text: { type: String, default: '' },
  placeholder: { type: String, default: '' },   // an empty line reads as a quiet invitation
  note: { type: String, default: '' },          // what the edit touches
  edited: { type: Boolean, default: false },
  oneLine: { type: Boolean, default: true },    // Enter = Done
  quote: { type: Boolean, default: false },     // “…” around the words
  size: { type: String, default: 'line' },      // line | small | title | step | block
  tag: { type: String, default: 'span' },       // 'div' when the words are a block (the chapter)
  label: { type: String, default: 'Change this line' },
  testid: { type: String, default: 'journal-inplace' },
  autostart: { type: Boolean, default: false },   // open as a field at once (adding a line from a ✎ menu)
})
const emit = defineEmits(['save', 'open', 'close'])

const open = ref(false)
const draft = ref('')
const field = ref(null)

function fit () { const el = field.value; if (!el) return; el.style.height = 'auto'; el.style.height = el.scrollHeight + 'px' }
async function start () {
  if (open.value) return
  draft.value = props.text
  open.value = true
  emit('open')
  await nextTick()
  const el = field.value
  if (el) {
    fit(); el.focus({ preventScroll: true })
    const n = el.value.length; try { el.setSelectionRange(n, n) } catch (e) { /* */ }
    // The field and its Cancel · Done in view (a line near the page's foot).
    const bar = el.parentElement && el.parentElement.querySelector('.donebar')
    if (bar && bar.scrollIntoView) bar.scrollIntoView({ block: 'nearest' })
  }
}
function cancel () { open.value = false; emit('close') }
function done () {
  const t = props.oneLine ? draft.value.replace(/\s+/g, ' ').trim() : draft.value.replace(/[ \t]+\n/g, '\n').trim()
  open.value = false
  emit('close')
  if (t !== props.text.trim()) emit('save', t)
}
function onKey (e) {
  if (e.key === 'Escape') { e.preventDefault(); e.stopPropagation(); cancel(); return }
  if (e.key === 'Enter' && props.oneLine && !e.shiftKey && !e.isComposing) { e.preventDefault(); done() }
}
onMounted(() => { if (props.autostart) start() })
defineExpose({ start, open })
</script>

<template>
  <span v-if="open" class="ip-open" :class="'sz-' + size" @click.stop @pointerdown.stop @pointerup.stop>
    <textarea ref="field" v-model="draft" class="inplace" rows="1" :aria-label="label" :data-testid="testid + '-field'" @input="fit" @keydown="onKey"></textarea>
    <span class="donebar">
      <span v-if="note" class="sp">{{ note }}</span>
      <button type="button" class="c" :data-testid="testid + '-cancel'" @click="cancel">Cancel</button>
      <button type="button" class="d" :data-testid="testid + '-done'" @click="done">Done</button>
    </span>
  </span>
  <component :is="tag" v-else class="ed" :class="['sz-' + size, { empty: !text }]" role="button" tabindex="0" :aria-label="`${label}: ${text || placeholder}`" :data-testid="testid"
             @click.stop="start" @keydown.enter.self.prevent="start" @keydown.space.self.prevent="start"><slot><template v-if="text">{{ quote ? `“${text}”` : text }}</template><template v-else>{{ placeholder }}</template></slot><span v-if="edited && text" class="yours" data-testid="journal-edited">edited</span><span class="pen" aria-hidden="true">✎</span></component>
</template>

<style scoped>
.ed { cursor: text; border-radius: 4px; transition: background .15s; -webkit-tap-highlight-color: transparent; }
.ed:focus-visible { background: rgba(201, 169, 78, .12); outline: 0; }
@media (hover: hover) { .ed:hover { background: rgba(201, 169, 78, .12); } }
.ed.empty { color: var(--j-ink3); }
.pen { display: inline-block; margin-left: 6px; font: normal 600 12px var(--j-ui); color: var(--j-hand); opacity: .55; vertical-align: 2px; }
/* A thread's steps (the prototype): the ✎ only on an empty line, or under the pointer. */
.sz-step:not(.empty) .pen { opacity: 0; transition: opacity .15s; }
.sz-step:focus-visible .pen { opacity: .55; }
@media (hover: hover) { .sz-step:hover .pen { opacity: .55; } }
.yours { margin-left: 6px; font: 600 9px var(--j-display); letter-spacing: .14em; text-transform: uppercase; color: var(--j-hand); opacity: .8; font-style: normal; }
.ip-open { display: block; }
.inplace { display: block; width: 100%; box-sizing: border-box; margin: 2px 0 0; padding: 4px 6px; border: 0; border-bottom: 1.5px dashed var(--j-hand); background: rgba(255, 250, 236, .85); border-radius: 4px 4px 0 0;
  font: inherit; color: var(--j-ink); resize: none; outline: none; box-shadow: 0 0 0 3px rgba(201, 169, 78, .18); overflow: hidden; min-height: 2.4em; }
.sz-title .inplace { font: italic 600 32px/1.1 var(--j-serif); min-height: 0; }
.sz-small .inplace { font-size: 16px; }
.sz-block .inplace { font: 500 18px/1.4 var(--j-serif); min-height: 12em; }
div.ed { display: block; }
.donebar { display: flex; justify-content: flex-end; align-items: center; gap: 10px; margin: 6px 0 4px; font: normal 500 12px/1.3 var(--j-ui); color: var(--j-ink3); letter-spacing: 0; text-transform: none; }
.donebar .sp { margin-right: auto; }
.donebar button { border: 0; cursor: pointer; font: 600 13px var(--j-ui); }
.donebar .c { color: var(--j-ink2); padding: 6px 4px; background: none; }
.donebar .d { color: #ffdfa3; background: linear-gradient(180deg, #7b4bb0, #4e2776); padding: 7px 16px; border-radius: 999px; }
</style>
